import type { CSSProperties } from 'react'
import React from 'react'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import classNames from 'classnames'
import { useApplyFinancingOffer, useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import { useApplyMFOFinancingOffer } from '@/components/composed/financing/hooks/useApplyMFOFinancingOffer'
import { Button, InjectHtml, TemporaryUnavailableTag } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import { isUserAgrarian } from '@/logic/auth/reducer'
import type { IApplyingFinancingOfferRequestBody, IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import { pushGtmEventOnRouteAuthFromFormOrder } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { ApplyingFinancingOfferSuccessModal } from '@/pages/Financing/components'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { useRegionsMicrocreditOrganizations } from '@/utils/hooks/applyingFinancingOffer/useRegionsMicrocreditOrganizations'
import { CustomizedForm } from '../../../forms'
import { addModalToWellKnown } from '../../../Modal/constants'
import type { ILandingApplyingOfferFormProps } from './interfaces'
import { messages } from './messages'
import { useApplyingOfferFormSteps } from './useApplyingOfferFormSteps'
import './styles.scss'

addModalToWellKnown({ ApplyingFinancingOfferSuccessModal })

const mapState = (state: RootState) => ({
  isAgrarian: isUserAgrarian()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

const LandingApplyingOfferForm: React.FC<ILandingApplyingOfferFormProps> = props => {
  const { formId, title, className = '', isDisabled = false, onlyButtonText, onActionButton, isDarkBackground = false } = props

  const { isAgrarian, userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const productId = props.productId
  const offerType = props.offerType
  const isCredit = offerType === EFinancingOfferType.CREDIT
  const shouldDisableButton = isAuthenticated && !isAgrarian
  const { applyMFOFianncingOffer } = useApplyMFOFinancingOffer()
  const { applyFinancingOffer } = useApplyFinancingOffer()
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()

  const steps = useApplyingOfferFormSteps(offerType, isAuthenticated, isDarkBackground)
  const { setOptions, formValues: values, profile } = useApplyingFinancingOfferForm({ productId, offerType })

  const { isMFOProduct } = useRegionsMicrocreditOrganizations({
    productId,
  })

  const handleSubmitForAuthenticatedUser = async (formValues: IFinancingOfferFormValues) => {
    const body = FormValuesFormatter[offerType]?.(formValues) || formValues

    if (!isApplyingOfferRequestBody(body)) {
      return
    }

    if (isMFOProduct) {
      await applyMFOFianncingOffer({ body, withSuccessModal: true })
    } else {
      await applyFinancingOffer({ body, offerType, withSuccessModal: true })
    }
  }

  const handleCloseAuthDialog = (formValues: IFinancingOfferFormValues) => () => {
    setOptions({ formValues })
  }

  const handleCloseAuthModal = (body: IFinancingOfferFormValues | IApplyingFinancingOfferRequestBody) => () => {
    if (isApplyingOfferRequestBody(body)) {
      applyLostFinancingOffer({ body, offerType, isLost: true })
    }
  }

  const handleSubmitForNonAuthenticatedUser = (formValues: IFinancingOfferFormValues) => {
    const body = FormValuesFormatter[offerType]?.(formValues) || formValues

    setOptions({ formValues: {} })
    pushGtmEventOnRouteAuthFromFormOrder(userGtmInfo, userRegion.name, null, productId)

    openAuthModal({
      shouldSendCodeImmediately: true,
      initialValues: { phone: formValues.phone, email: formValues.email },
      onCloseDialog: handleCloseAuthDialog(formValues),
      onCloseModal: handleCloseAuthModal(body),
    })
  }

  const handleSubmit = async (formValues: IFinancingOfferFormValues) => {
    if (isAuthenticated) {
      await handleSubmitForAuthenticatedUser(formValues)
    } else {
      await handleSubmitForNonAuthenticatedUser(formValues)
    }
  }

  return (
    <section
      id={formId}
      className={classNames('financing-offer-form-container', className, { 'financing-offer-form-container_is-credit': isCredit })}
      style={{ '--active-dot-style': EColor.WHITE } as CSSProperties}
    >
      <div className="page-wrapper financing-offer-form-container__wrapper">
        <div className={classNames({ 'financing-offer-form-container_row-heading': isCredit })}>
          <InjectHtml
            TagName="h2"
            html={title}
            className="space-holder40 space-holder32-sm financing-offer-form-container__title color_white"
          />
          {isCredit && <p className="financing-offer-form-container__subtitle space-holder32-sm">{messages.sign}</p>}
          {isDisabled && <TemporaryUnavailableTag className="space-holder-top48-from-sm space-holder16-md" />}
        </div>
        {onlyButtonText ? (
          <div>
            <Button modifiers={['white']} onClick={onActionButton}>
              {onlyButtonText}
            </Button>
          </div>
        ) : (
          <CustomizedForm<IFinancingOfferFormValues>
            initialValues={getApplyingFinancingOfferFormInitialValues({ offerType, productId, profile, formValues: values })}
            onSubmit={handleSubmit}
            steps={steps}
            // TODO: убрать хранимое в redux состояние запроса https://jira.poleinvest.ru/browse/IFA-2085
            requestStatusName="mockedRequestStatus"
            tooltipText={messages.forAgrariansOnly}
            shouldDisableButton={shouldDisableButton}
            ignoreValidationForSubmitButton
            shouldUseLocalRequestState
          />
        )}
      </div>
    </section>
  )
}

export default LandingApplyingOfferForm
